import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Toaster, toast } from "sonner";
import {
  Lock, LogOut, Plus, Pencil, Trash2, Star, Eye, EyeOff,
  Package, Truck, Clock, X, Upload, MessageCircle, ExternalLink, Image as ImageIcon, Type, Tag,
} from "lucide-react";
import {
  adminLogin, adminMe, adminListProducts, adminCreateProduct,
  adminUpdateProduct, adminDeleteProduct, adminUploadImage, adminSaveSite,
  getToken, clearToken, formatApiError,
} from "@/lib/admin";
import { getSiteSettings, mergeSite } from "@/lib/site";
import { waProduct } from "@/lib/whatsapp";

const MARCAS = ["Polo Ralph Lauren", "Tommy Hilfiger", "Bespro Shop", "Abercrombie & Fitch", "Cabela's", "Bass Pro Shops", "RedHead", "Nike", "Reserva", "Acostamento", "Aleatory", "Polo Play", "Outras"];
const SUBCATEGORIAS = ["Dad Hat", "Aba Curva", "Trucker", "Camiseta", "Bermuda", "Moletom", "Jaqueta"];

const EMPTY_FORM = {
  nome: "", marca: "Polo Ralph Lauren", categoria: "Bonés", subcategoria: "Dad Hat",
  cor: "", imagem: "", galeria: [], codigo: "", descricao: "",
  disponibilidade: "pronta_entrega", destaque: false, ativo: true, importado: false,
};

const IMAGE_SLOTS = [
  { key: "heroBg", label: "Fundo do topo da página inicial" },
  { key: "heroFg", label: "Foto em destaque do topo" },
  { key: "catBones", label: "Card — Bonés" },
  { key: "catRoupas", label: "Card — Bonés Importados" },
  { key: "catEncomendas", label: "Card — Encomendas" },
  { key: "encomendasBg", label: "Fundo da seção Encomendas" },
  { key: "roupasHero", label: "Topo da página Bonés Importados" },
  { key: "catalogoHero", label: "Topo da página Catálogo" },
];

const TEXT_SLOTS = [
  { key: "heroL1", label: "Título principal — linha 1" },
  { key: "heroL2Prefix", label: "Título principal — linha 2" },
  { key: "heroL2Accent", label: "Palavra em destaque (contorno)" },
  { key: "heroSubtitle", label: "Subtítulo do topo", textarea: true },
  { key: "heroProof", label: "Linha de prova (+1.000 clientes...)" },
];

const inputCls =
  "w-full rounded-lg border border-white/15 bg-graphite px-4 py-3.5 text-sm text-offwhite placeholder:text-zinc-600 focus:border-silver/60 focus:outline-none";

const Field = ({ label, children, testid }) => (
  <label className="block" data-testid={testid}>
    <span className="mb-1.5 block text-[10px] font-semibold uppercase tracking-[0.25em] text-zinc-500">{label}</span>
    {children}
  </label>
);

const LoginScreen = ({ onLogin }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const user = await adminLogin(email, password);
      onLogin(user);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-obsidian px-5">
      <motion.form
        onSubmit={submit}
        initial={{ y: 24, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        data-testid="admin-login-form"
        className="w-full max-w-sm rounded-2xl border border-white/10 bg-coal p-8"
      >
        <div className="mx-auto mb-6 flex h-14 w-14 items-center justify-center rounded-full border border-silver/30">
          <Lock className="h-6 w-6 text-silver" />
        </div>
        <h1 className="text-center font-display text-2xl font-extrabold uppercase tracking-tight">Painel DM Imports</h1>
        <p className="mt-2 text-center text-xs text-zinc-500">Acesso restrito ao proprietário</p>

        <div className="mt-8 space-y-4">
          <Field label="E-mail" testid="admin-email-field">
            <input
              type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
              data-testid="admin-email-input" placeholder="admin@dmimports.com" className={inputCls}
            />
          </Field>
          <Field label="Senha" testid="admin-password-field">
            <input
              type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
              data-testid="admin-password-input" placeholder="••••••••" className={inputCls}
            />
          </Field>
        </div>

        {error && (
          <p data-testid="admin-login-error" className="mt-4 rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-3 text-xs text-red-300">
            {error}
          </p>
        )}

        <button
          type="submit" disabled={loading} data-testid="admin-login-button"
          className="mt-6 w-full rounded-full bg-offwhite py-4 text-xs font-bold uppercase tracking-[0.2em] text-obsidian transition-transform duration-300 hover:scale-[1.02] disabled:opacity-50"
        >
          {loading ? "Entrando..." : "Entrar"}
        </button>
      </motion.form>
    </div>
  );
};

const ProductForm = ({ initial, onSave, onClose }) => {
  const [form, setForm] = useState(initial);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const url = await adminUploadImage(file);
      set("imagem", url);
      toast.success("Foto enviada!");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setUploading(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.imagem) {
      toast.error("Adicione uma foto do produto.");
      return;
    }
    setSaving(true);
    try {
      await onSave({ ...form, importado: form.categoria === "Bonés Importados" });
    } finally {
      setSaving(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-[80] overflow-y-auto bg-obsidian/95 backdrop-blur-xl"
      data-testid="product-form-modal"
    >
      <div className="mx-auto max-w-lg px-5 py-10">
        <div className="mb-6 flex items-center justify-between">
          <h2 className="font-display text-xl font-extrabold uppercase tracking-tight">
            {initial.id ? "Editar produto" : "Novo produto"}
          </h2>
          <button onClick={onClose} data-testid="product-form-close" aria-label="Fechar" className="flex h-11 w-11 items-center justify-center rounded-full border border-white/15">
            <X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={submit} className="space-y-4">
          <div className="overflow-hidden rounded-xl border border-white/10 bg-graphite">
            {form.imagem ? (
              <img src={form.imagem} alt="Foto do produto" data-testid="product-form-image-preview" className="aspect-[4/5] w-full object-cover" />
            ) : (
              <div className="flex aspect-[4/5] w-full flex-col items-center justify-center gap-2 text-zinc-600">
                <Upload className="h-8 w-8" />
                <span className="text-xs uppercase tracking-[0.2em]">Sem foto</span>
              </div>
            )}
          </div>
          <label
            data-testid="product-form-upload-button"
            className="flex cursor-pointer items-center justify-center gap-2 rounded-full border border-silver/40 py-3.5 text-[11px] font-bold uppercase tracking-[0.18em] text-offwhite transition-colors hover:bg-offwhite hover:text-obsidian"
          >
            <Upload className="h-4 w-4" />
            {uploading ? "Enviando foto..." : form.imagem ? "Trocar foto" : "Enviar foto do celular"}
            <input type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={handleFile} data-testid="product-form-file-input" />
          </label>
          <Field label="Ou cole a URL da imagem" testid="product-form-imagem-url">
            <input value={form.imagem} onChange={(e) => set("imagem", e.target.value)} data-testid="product-form-imagem-input" placeholder="https://..." className={inputCls} />
          </Field>

          <Field label="Galeria de fotos (opcional)" testid="product-form-galeria">
            <div className="space-y-2">
              {(form.galeria || []).map((url, i) => (
                <div key={i} className="flex items-center gap-2">
                  <img src={url} alt={`Foto extra ${i + 1}`} className="h-12 w-12 shrink-0 rounded-lg border border-white/10 object-cover" />
                  <input
                    value={url}
                    onChange={(e) => set("galeria", form.galeria.map((g, idx) => (idx === i ? e.target.value : g)))}
                    data-testid={`product-form-galeria-url-${i + 1}`}
                    className={`${inputCls} !py-2.5 text-[11px]`}
                  />
                  <button
                    type="button"
                    onClick={() => set("galeria", form.galeria.filter((_, idx) => idx !== i))}
                    data-testid={`product-form-galeria-remove-${i + 1}`}
                    aria-label={`Remover foto ${i + 1}`}
                    className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-red-500/30 text-red-400"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>
              ))}
              <label
                data-testid="product-form-galeria-upload"
                className="flex cursor-pointer items-center justify-center gap-2 rounded-full border border-dashed border-white/20 py-3 text-[10px] font-bold uppercase tracking-[0.15em] text-zinc-400 transition-colors hover:border-silver/50 hover:text-offwhite"
              >
                <Plus className="h-3.5 w-3.5" /> Adicionar foto à galeria
                <input
                  type="file" accept="image/jpeg,image/png,image/webp" className="hidden" data-testid="product-form-galeria-file"
                  onChange={async (e) => {
                    const file = e.target.files?.[0];
                    if (!file) return;
                    setUploading(true);
                    try {
                      const url = await adminUploadImage(file);
                      set("galeria", [...(form.galeria || []), url]);
                      toast.success("Foto adicionada à galeria!");
                    } catch (err) {
                      toast.error(formatApiError(err.response?.data?.detail));
                    } finally {
                      setUploading(false);
                    }
                  }}
                />
              </label>
            </div>
          </Field>

          <Field label="Nome do produto *" testid="product-form-nome">
            <input required value={form.nome} onChange={(e) => set("nome", e.target.value)} data-testid="product-form-nome-input" placeholder="Boné Polo Ralph Lauren Preto" className={inputCls} />
          </Field>

          <div className="grid grid-cols-2 gap-4">
            <Field label="Marca *" testid="product-form-marca">
              <select value={form.marca} onChange={(e) => set("marca", e.target.value)} data-testid="product-form-marca-select" className={inputCls}>
                {MARCAS.map((m) => <option key={m} value={m}>{m}</option>)}
              </select>
            </Field>
            <Field label="Categoria *" testid="product-form-categoria">
              <select value={form.categoria} onChange={(e) => set("categoria", e.target.value)} data-testid="product-form-categoria-select" className={inputCls}>
                <option value="Bonés Básicos">Bonés Básicos</option>
                <option value="Bonés Premium">Bonés Premium</option>
                <option value="Bonés Importados">Bonés Importados</option>
                <option value="Roupas">Roupas</option>
              </select>
            </Field>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <Field label="Subcategoria" testid="product-form-subcategoria">
              <select value={form.subcategoria} onChange={(e) => set("subcategoria", e.target.value)} data-testid="product-form-subcategoria-select" className={inputCls}>
                {SUBCATEGORIAS.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </Field>
            <Field label="Cor" testid="product-form-cor">
              <input value={form.cor} onChange={(e) => set("cor", e.target.value)} data-testid="product-form-cor-input" placeholder="Preto" className={inputCls} />
            </Field>
          </div>

          <Field label="Descrição" testid="product-form-descricao">
            <textarea value={form.descricao} onChange={(e) => set("descricao", e.target.value)} data-testid="product-form-descricao-input" rows={3} placeholder="Detalhes do modelo..." className={inputCls} />
          </Field>

          <div className="grid grid-cols-2 gap-4">
            <Field label="Código (opcional)" testid="product-form-codigo">
              <input value={form.codigo} onChange={(e) => set("codigo", e.target.value)} data-testid="product-form-codigo-input" placeholder="Automático" className={inputCls} />
            </Field>
            <Field label="Disponibilidade" testid="product-form-disponibilidade">
              <select value={form.disponibilidade} onChange={(e) => set("disponibilidade", e.target.value)} data-testid="product-form-disponibilidade-select" className={inputCls}>
                <option value="pronta_entrega">Pronta Entrega</option>
                <option value="encomenda">Encomenda</option>
              </select>
            </Field>
          </div>

          <div className="flex gap-3">
            <button
              type="button" onClick={() => set("destaque", !form.destaque)} data-testid="product-form-destaque-toggle"
              className={`flex flex-1 items-center justify-center gap-2 rounded-full border py-3.5 text-[11px] font-bold uppercase tracking-[0.15em] transition-colors ${form.destaque ? "border-silver bg-offwhite text-obsidian" : "border-white/15 text-zinc-400"}`}
            >
              <Star className="h-4 w-4" fill={form.destaque ? "currentColor" : "none"} /> Destaque
            </button>
            <button
              type="button" onClick={() => set("ativo", !form.ativo)} data-testid="product-form-ativo-toggle"
              className={`flex flex-1 items-center justify-center gap-2 rounded-full border py-3.5 text-[11px] font-bold uppercase tracking-[0.15em] transition-colors ${form.ativo ? "border-silver bg-offwhite text-obsidian" : "border-white/15 text-zinc-400"}`}
            >
              {form.ativo ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />} {form.ativo ? "Visível" : "Oculto"}
            </button>
          </div>

          <button
            type="submit" disabled={saving || uploading} data-testid="product-form-save-button"
            className="w-full rounded-full bg-offwhite py-4 text-xs font-bold uppercase tracking-[0.2em] text-obsidian transition-transform duration-300 hover:scale-[1.01] disabled:opacity-50"
          >
            {saving ? "Salvando..." : initial.id ? "Salvar alterações" : "Cadastrar produto"}
          </button>
        </form>
      </div>
    </motion.div>
  );
};

const ImageSlotEditor = ({ label, value, onChange, testid }) => {
  const [uploading, setUploading] = useState(false);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const url = await adminUploadImage(file);
      onChange(url);
      toast.success("Foto enviada! Salve para aplicar.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="rounded-xl border border-white/10 bg-coal p-3" data-testid={testid}>
      <p className="mb-2.5 text-[10px] font-semibold uppercase tracking-[0.2em] text-zinc-500">{label}</p>
      <div className="flex items-center gap-3">
        <img src={value} alt={label} className="h-16 w-16 shrink-0 rounded-lg border border-white/10 object-cover" />
        <div className="flex min-w-0 flex-1 flex-col gap-2">
          <label className="flex cursor-pointer items-center justify-center gap-2 rounded-full border border-silver/40 py-2.5 text-[10px] font-bold uppercase tracking-[0.15em] text-offwhite transition-colors hover:bg-offwhite hover:text-obsidian">
            <Upload className="h-3.5 w-3.5" /> {uploading ? "Enviando..." : "Trocar foto"}
            <input type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={handleFile} data-testid={`${testid}-upload`} />
          </label>
          <input
            value={value} onChange={(e) => onChange(e.target.value)} data-testid={`${testid}-url`}
            className="w-full truncate rounded-lg border border-white/10 bg-graphite px-3 py-2 text-[11px] text-zinc-400 focus:border-silver/50 focus:outline-none"
          />
        </div>
      </div>
    </div>
  );
};

const SitePanel = () => {
  const [site, setSite] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    getSiteSettings()
      .then((saved) => setSite(mergeSite(saved)))
      .catch(() => setSite(mergeSite({})));
  }, []);

  if (!site) return <p className="py-10 text-center text-sm text-zinc-500">Carregando configurações...</p>;

  const setImg = (key, url) => setSite((s) => ({ ...s, images: { ...s.images, [key]: url } }));
  const setInsta = (i, url) => setSite((s) => ({ ...s, instagram: s.instagram.map((v, idx) => (idx === i ? url : v)) }));
  const setText = (key, v) => setSite((s) => ({ ...s, texts: { ...s.texts, [key]: v } }));

  const save = async () => {
    setSaving(true);
    try {
      await adminSaveSite(site);
      toast.success("Site atualizado! As mudanças já estão no ar.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="site-panel" className="mt-6 space-y-8 pb-6">
      <div>
        <p className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.25em] text-silver">
          <ImageIcon className="h-4 w-4" /> Fotos do site
        </p>
        <div className="mt-4 space-y-3">
          {IMAGE_SLOTS.map((slot) => (
            <ImageSlotEditor key={slot.key} label={slot.label} value={site.images[slot.key]} onChange={(url) => setImg(slot.key, url)} testid={`site-img-${slot.key}`} />
          ))}
        </div>
      </div>

      <div>
        <p className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.25em] text-silver">
          <ImageIcon className="h-4 w-4" /> Grade do Instagram (6 fotos)
        </p>
        <div className="mt-4 space-y-3">
          {site.instagram.map((img, i) => (
            <ImageSlotEditor key={i} label={`Foto ${i + 1} do feed`} value={img} onChange={(url) => setInsta(i, url)} testid={`site-insta-${i + 1}`} />
          ))}
        </div>
      </div>

      <div>
        <p className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.25em] text-silver">
          <Type className="h-4 w-4" /> Textos principais
        </p>
        <div className="mt-4 space-y-4">
          {TEXT_SLOTS.map((slot) => (
            <Field key={slot.key} label={slot.label} testid={`site-text-${slot.key}`}>
              {slot.textarea ? (
                <textarea rows={2} value={site.texts[slot.key]} onChange={(e) => setText(slot.key, e.target.value)} data-testid={`site-text-${slot.key}-input`} className={inputCls} />
              ) : (
                <input value={site.texts[slot.key]} onChange={(e) => setText(slot.key, e.target.value)} data-testid={`site-text-${slot.key}-input`} className={inputCls} />
              )}
            </Field>
          ))}
        </div>
      </div>

      <div>
        <p className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.25em] text-silver">
          <Tag className="h-4 w-4" /> Marcas (uma por linha)
        </p>
        <textarea
          rows={5} value={site.brands.join("\n")}
          onChange={(e) => setSite((s) => ({ ...s, brands: e.target.value.split("\n").map((b) => b.trim()).filter(Boolean) }))}
          data-testid="site-brands-input" className={`${inputCls} mt-4`}
        />
      </div>

      <div>
        <p className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-[0.25em] text-silver">
          <MessageCircle className="h-4 w-4" /> WhatsApp da loja
        </p>
        <div className="mt-4">
          <Field label="Número com código do país e DDD (só números)" testid="site-whatsapp">
            <input
              value={site.whatsapp}
              onChange={(e) => setSite((s) => ({ ...s, whatsapp: e.target.value.replace(/\D/g, "") }))}
              data-testid="site-whatsapp-input"
              placeholder="5567996863443"
              inputMode="numeric"
              className={inputCls}
            />
          </Field>
          <p className="mt-2 text-[11px] leading-relaxed text-zinc-500">
            Todas as mensagens do site (produtos, encomendas, botões) serão enviadas para este número. Ex.: 5567996863443
          </p>
        </div>
      </div>

      <button
        onClick={save} disabled={saving} data-testid="site-save-button"
        className="sticky bottom-5 w-full rounded-full bg-offwhite py-4 text-xs font-bold uppercase tracking-[0.2em] text-obsidian shadow-2xl transition-transform duration-300 hover:scale-[1.01] disabled:opacity-50"
      >
        {saving ? "Salvando..." : "Salvar alterações do site"}
      </button>
    </div>
  );
};

const Dashboard = ({ user, onLogout }) => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [tab, setTab] = useState("produtos");

  const load = async () => {
    try {
      setProducts(await adminListProducts());
    } catch (err) {
      if (err.response?.status === 401) onLogout();
      else toast.error("Erro ao carregar produtos");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const save = async (form) => {
    try {
      if (form.id) {
        await adminUpdateProduct(form.id, form);
        toast.success("Produto atualizado!");
      } else {
        await adminCreateProduct(form);
        toast.success("Produto cadastrado!");
      }
      setEditing(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const toggle = async (p, key) => {
    try {
      await adminUpdateProduct(p.id, { ...p, [key]: !p[key] });
      load();
    } catch {
      toast.error("Erro ao atualizar");
    }
  };

  const remove = async (p) => {
    if (!window.confirm(`Excluir "${p.nome}"? Essa ação não pode ser desfeita.`)) return;
    try {
      await adminDeleteProduct(p.id);
      toast.success("Produto excluído");
      load();
    } catch {
      toast.error("Erro ao excluir");
    }
  };

  const stats = [
    { icon: Package, label: "Produtos", value: products.length, id: "stat-total" },
    { icon: Truck, label: "Pronta entrega", value: products.filter((p) => p.disponibilidade === "pronta_entrega").length, id: "stat-pe" },
    { icon: Clock, label: "Encomenda", value: products.filter((p) => p.disponibilidade === "encomenda").length, id: "stat-enc" },
    { icon: Star, label: "Destaques", value: products.filter((p) => p.destaque).length, id: "stat-dest" },
  ];

  return (
    <div className="min-h-screen bg-obsidian pb-24" data-testid="admin-dashboard">
      <header className="glass-dark sticky top-0 z-40 border-b border-white/10">
        <div className="mx-auto flex h-16 max-w-3xl items-center justify-between px-5">
          <p className="font-display text-sm font-extrabold tracking-[0.18em]">PAINEL · DM IMPORTS</p>
          <div className="flex items-center gap-2">
            <a href="/" target="_blank" rel="noopener noreferrer" data-testid="admin-view-site" aria-label="Ver site" className="flex h-10 w-10 items-center justify-center rounded-full border border-white/15 text-zinc-400 hover:text-offwhite">
              <ExternalLink className="h-4 w-4" />
            </a>
            <button onClick={onLogout} data-testid="admin-logout-button" aria-label="Sair" className="flex h-10 w-10 items-center justify-center rounded-full border border-white/15 text-zinc-400 hover:text-offwhite">
              <LogOut className="h-4 w-4" />
            </button>
          </div>
        </div>
      </header>

      <div className="mx-auto max-w-3xl px-5 pt-8">
        <p className="text-xs text-zinc-500">Olá, {user.name}</p>

        <div className="mt-4 grid grid-cols-2 gap-2" data-testid="admin-tabs">
          {[
            ["produtos", "Produtos"],
            ["site", "Personalizar Site"],
          ].map(([id, label]) => (
            <button
              key={id} onClick={() => setTab(id)} data-testid={`admin-tab-${id}`}
              className={`rounded-full border py-3 text-[11px] font-bold uppercase tracking-[0.18em] transition-colors ${
                tab === id ? "border-offwhite bg-offwhite text-obsidian" : "border-white/15 text-zinc-400 hover:text-offwhite"
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        {tab === "produtos" && (
          <>
            <h1 className="mt-6 font-display text-2xl font-extrabold uppercase tracking-tight">Seus produtos</h1>

            <div className="mt-6 grid grid-cols-4 gap-2 sm:gap-3">
              {stats.map((s) => (
                <div key={s.id} data-testid={`admin-${s.id}`} className="rounded-xl border border-white/10 bg-coal p-3 text-center sm:p-4">
                  <s.icon className="mx-auto h-4 w-4 text-silver" />
                  <p className="mt-2 font-display text-lg font-extrabold sm:text-2xl">{s.value}</p>
                  <p className="text-[9px] uppercase tracking-[0.15em] text-zinc-500 sm:text-[10px]">{s.label}</p>
                </div>
              ))}
            </div>

            <button
              onClick={() => setEditing({ ...EMPTY_FORM })} data-testid="admin-new-product-button"
              className="mt-6 flex w-full items-center justify-center gap-2 rounded-full bg-offwhite py-4 text-xs font-bold uppercase tracking-[0.2em] text-obsidian transition-transform duration-300 hover:scale-[1.01]"
            >
              <Plus className="h-4 w-4" /> Novo produto
            </button>

            <div className="mt-8 space-y-3" data-testid="admin-product-list">
              {loading && <p className="py-10 text-center text-sm text-zinc-500">Carregando...</p>}
              {!loading && products.length === 0 && (
                <p className="py-10 text-center text-sm text-zinc-500">Nenhum produto cadastrado ainda.</p>
              )}
              {products.map((p) => (
                <div
                  key={p.id} data-testid={`admin-product-row-${p.codigo}`}
                  className={`flex items-center gap-4 rounded-xl border border-white/10 bg-coal p-3 ${!p.ativo ? "opacity-50" : ""}`}
                >
                  <img src={p.imagem} alt={p.nome} className="h-16 w-14 shrink-0 rounded-lg object-cover" />
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-sm font-bold">{p.nome}</p>
                    <p className="mt-0.5 truncate text-[11px] text-zinc-500">
                      {p.codigo} · {p.marca} · {p.disponibilidade === "pronta_entrega" ? "Pronta Entrega" : "Encomenda"}
                    </p>
                  </div>
                  <div className="flex shrink-0 flex-col gap-1.5 sm:flex-row">
                    <button onClick={() => toggle(p, "destaque")} data-testid={`admin-toggle-destaque-${p.codigo}`} aria-label="Alternar destaque" className={`flex h-9 w-9 items-center justify-center rounded-full border ${p.destaque ? "border-silver/60 text-silver" : "border-white/10 text-zinc-600"}`}>
                      <Star className="h-4 w-4" fill={p.destaque ? "currentColor" : "none"} />
                    </button>
                    <button onClick={() => toggle(p, "ativo")} data-testid={`admin-toggle-ativo-${p.codigo}`} aria-label="Alternar visibilidade" className="flex h-9 w-9 items-center justify-center rounded-full border border-white/10 text-zinc-400">
                      {p.ativo ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
                    </button>
                    <a href={waProduct(p.nome, p.codigo)} target="_blank" rel="noopener noreferrer" data-testid={`admin-test-whatsapp-${p.codigo}`} aria-label="Testar link WhatsApp" className="flex h-9 w-9 items-center justify-center rounded-full border border-white/10 text-zinc-400">
                      <MessageCircle className="h-4 w-4" />
                    </a>
                    <button onClick={() => setEditing({ ...EMPTY_FORM, ...p })} data-testid={`admin-edit-${p.codigo}`} aria-label="Editar" className="flex h-9 w-9 items-center justify-center rounded-full border border-white/10 text-zinc-400">
                      <Pencil className="h-4 w-4" />
                    </button>
                    <button onClick={() => remove(p)} data-testid={`admin-delete-${p.codigo}`} aria-label="Excluir" className="flex h-9 w-9 items-center justify-center rounded-full border border-red-500/30 text-red-400">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        {tab === "site" && <SitePanel />}
      </div>

      <AnimatePresence>
        {editing && <ProductForm initial={editing} onSave={save} onClose={() => setEditing(null)} />}
      </AnimatePresence>
    </div>
  );
};

const Admin = () => {
  const [user, setUser] = useState(null);

  useEffect(() => {
    document.title = "Painel Admin | DM IMPORTS";
    if (!getToken()) {
      setUser(false);
      return;
    }
    adminMe()
      .then(setUser)
      .catch(() => {
        clearToken();
        setUser(false);
      });
  }, []);

  const logout = () => {
    clearToken();
    setUser(false);
  };

  return (
    <>
      <Toaster theme="dark" position="top-center" richColors />
      {user === null && (
        <div className="flex min-h-screen items-center justify-center bg-obsidian">
          <p className="text-sm text-zinc-500">Verificando acesso...</p>
        </div>
      )}
      {user === false && <LoginScreen onLogin={setUser} />}
      {user && <Dashboard user={user} onLogout={logout} />}
    </>
  );
};

export default Admin;
